<template>
  <div>
    <v-container fluid>
      <v-card elevation="3" rounded="xl">
        <v-card-title class="py-4 px-6">
          <v-row class="w-100 ma-0" align="center">
            <v-col cols="12" md="6" class="pa-0">
              <h2 class="text-h5 font-weight-bold primary--text">RMC Bookings</h2>
              <p class="text-body-2 grey--text ma-0">View and manage all plant bookings</p>
            </v-col>
            <v-col cols="12" md="6" class="pa-0 text-right">
              <v-chip color="primary" outlined>
                <v-icon left small>mdi-bookmark-multiple</v-icon>
                {{ count }} Bookings
              </v-chip>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="mx-6"></v-divider>

        <v-card-subtitle class="px-6 pt-4 pb-2">
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="filter.search" label="Search builder / plant..." prepend-inner-icon="mdi-magnify" outlined rounded dense clearable @keydown.enter="getData" @click:clear="filter.search = ''; getData()"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-select v-model="filter.status" :items="statuses" label="Status" outlined rounded dense clearable @change="getData"></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-select v-model="filter.booking_type" :items="types" label="Type" outlined rounded dense clearable @change="getData"></v-select>
            </v-col>
          </v-row>
        </v-card-subtitle>

        <v-card-text class="pa-0">
          <table-skeleton v-if="isLoading" :rows="8" :cols="7" />

          <v-data-table v-else :headers="headers" :items="filteredBookings" hide-default-footer>
            <template #[`item.sr_no`]="{ index }">
              <span class="font-weight-medium">{{ index + 1 }}</span>
            </template>

            <template #[`item.status`]="{ item }">
              <v-chip :color="statusColor(item.status)" small dark>{{ item.status }}</v-chip>
            </template>

            <template #[`item.booking_type`]="{ item }">
              <v-chip small outlined color="info">{{ item.booking_type }}</v-chip>
            </template>

            <template #[`item.plant`]="{ item }">
              <span v-if="item.plant">{{ item.plant.name }}</span>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.user`]="{ item }">
              <div v-if="item.user">
                <div>{{ item.user.first_name || item.user.user_name }}</div>
                <div class="text-caption grey--text">{{ item.user.phone || '' }}</div>
              </div>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.dates`]="{ item }">
              <div class="text-caption">
                <div>{{ formatDate(item.start_date) }}</div>
                <div>to {{ formatDate(item.end_date) }}</div>
              </div>
            </template>

            <template #[`item.capacity`]="{ item }">
              <span class="text-caption font-weight-medium">{{ item.capacity_m3 ? parseFloat(item.capacity_m3).toFixed(0) + ' m³' : '—' }}</span>
            </template>

            <template #[`item.amount`]="{ item }">
              <span class="font-weight-medium">₹{{ formatNumber(item.total_amount) }}</span>
            </template>

            <template #[`item.payment`]="{ item }">
              <v-chip :color="paymentColor(item.payment_status)" x-small dark @click="item.transaction_id ? showPaymentDetails(item) : null" :style="item.transaction_id ? 'cursor:pointer' : ''">
                {{ item.payment_status || 'unpaid' }}
              </v-chip>
            </template>

            <template #[`item.delivery_address`]="{ item }">
              <div v-if="item.delivery_address" class="text-caption" style="max-width:180px">
                <v-icon x-small color="primary">mdi-map-marker</v-icon>
                {{ item.delivery_address }}
              </div>
              <span v-else class="grey--text text-caption">Not set</span>
            </template>

            <template #[`item.actions`]="{ item }">
              <div class="d-flex align-center">
                <div v-if="item.status === 'pending'">
                  <v-btn x-small color="success" class="mr-1" @click="approve(item.id)">Approve</v-btn>
                  <v-btn x-small color="error" outlined @click="reject(item.id)">Reject</v-btn>
                </div>
                <v-btn v-if="item.status === 'confirmed' || item.status === 'active'" x-small color="primary" outlined class="ml-1" @click="viewDelivery(item.id)">
                  <v-icon x-small left>mdi-truck</v-icon>Delivery
                </v-btn>
              </div>
            </template>

            <template #no-data>
              <div class="text-center py-12">
                <v-icon size="60" color="grey lighten-3">mdi-bookmark-multiple</v-icon>
                <h3 class="grey--text mt-4">No Bookings Found</h3>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Payment Details Dialog -->
      <v-dialog v-model="showPaymentDialog" max-width="500">
        <v-card rounded="lg" v-if="selectedBooking">
          <v-card-title class="pa-6 pb-3">
            <v-icon left color="primary">mdi-credit-card-outline</v-icon>
            Payment Details
          </v-card-title>
          <v-card-text class="px-6 pb-2">
            <div class="mb-3">
              <span class="grey--text text-caption">Status</span><br>
              <v-chip :color="paymentColor(selectedBooking.payment_status)" small dark>{{ selectedBooking.payment_status || 'unpaid' }}</v-chip>
            </div>
            <div class="mb-3" v-if="selectedBooking.transaction_id">
              <span class="grey--text text-caption">Transaction / UTR ID</span><br>
              <span class="font-weight-medium">{{ selectedBooking.transaction_id }}</span>
            </div>
            <div class="mb-3">
              <span class="grey--text text-caption">Amount</span><br>
              <span class="font-weight-bold text-h6 primary--text">₹{{ formatNumber(selectedBooking.total_amount) }}</span>
            </div>
            <div class="mb-3" v-if="selectedBooking.plant">
              <span class="grey--text text-caption">Plant</span><br>
              <span class="font-weight-medium">{{ selectedBooking.plant.name }}</span>
            </div>
            <div class="mb-3" v-if="selectedBooking.user">
              <span class="grey--text text-caption">Builder</span><br>
              <span class="font-weight-medium">{{ selectedBooking.user.first_name || '' }} {{ selectedBooking.user.last_name || '' }}</span>
              <span class="grey--text ml-2" v-if="selectedBooking.user.phone">{{ selectedBooking.user.phone }}</span>
            </div>
            <div class="mb-3" v-if="selectedBooking.payment_proof">
              <span class="grey--text text-caption">Payment Proof</span><br>
              <a :href="getProofUrl(selectedBooking.payment_proof)" target="_blank">
                <v-img :src="getProofUrl(selectedBooking.payment_proof)" max-height="250" contain class="mt-2 rounded-lg" style="border: 1px solid #eee;"></v-img>
              </a>
            </div>
            <div class="mb-3" v-if="selectedBooking.special_requirements">
              <span class="grey--text text-caption">Special Requirements</span><br>
              <span class="font-weight-medium">{{ selectedBooking.special_requirements }}</span>
            </div>
            <div class="mb-3" v-if="selectedBooking.mix_design">
              <span class="grey--text text-caption">Mix Design</span><br>
              <v-chip small color="blue darken-1" dark class="mt-1 mb-2">Grade {{ selectedBooking.mix_design.grade }}</v-chip>
              <v-simple-table dense class="mt-1" style="border:1px solid #eee; border-radius:8px;">
                <template #default>
                  <tbody>
                    <tr v-for="(val, key) in selectedBooking.mix_design.ingredients" :key="key">
                      <td class="text-caption grey--text py-1">{{ key }}</td>
                      <td class="text-caption font-weight-medium py-1">{{ val }} kg</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-card-text>
          <v-card-actions class="px-6 pb-6">
            <v-btn v-if="selectedBooking.payment_status === 'submitted'" small color="error" outlined @click="rejectPaymentAction(selectedBooking.id)" class="mr-2">Reject Payment</v-btn>
            <v-btn v-if="selectedBooking.payment_status === 'submitted'" small color="success" @click="verifyPaymentAction(selectedBooking.id)">Verify Payment</v-btn>
            <v-spacer></v-spacer>
            <v-btn text @click="showPaymentDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delivery Details Dialog -->
      <v-dialog v-model="showDeliveryDialog" max-width="480">
        <v-card rounded="lg" v-if="selectedDelivery">
          <v-card-title class="pa-6 pb-3">
            <v-icon left color="primary">mdi-truck-delivery</v-icon>
            Delivery Status
          </v-card-title>
          <v-card-text class="px-6 pb-2">
            <div class="mb-3">
              <span class="grey--text text-caption">Status</span><br>
              <v-chip :color="deliveryStatusColor(selectedDelivery.status)" small dark>{{ selectedDelivery.status }}</v-chip>
            </div>
            <div class="mb-3" v-if="selectedDelivery.delivery_address">
              <span class="grey--text text-caption">Delivery Address</span><br>
              <span class="font-weight-medium">{{ selectedDelivery.delivery_address }}</span>
            </div>
            <div class="mb-3" v-if="selectedDelivery.estimated_time">
              <span class="grey--text text-caption">ETA</span><br>
              <span class="font-weight-medium success--text">{{ selectedDelivery.estimated_time }}</span>
            </div>
            <div class="mb-3" v-if="selectedDelivery.dispatched_at">
              <span class="grey--text text-caption">Dispatched At</span><br>
              <span>{{ selectedDelivery.dispatched_at.substring(0, 16) }}</span>
            </div>
            <div class="mb-3" v-if="selectedDelivery.delivered_at">
              <span class="grey--text text-caption">Delivered At</span><br>
              <span class="success--text font-weight-medium">{{ selectedDelivery.delivered_at.substring(0, 16) }}</span>
            </div>
            <div class="mb-3" v-if="selectedDelivery.failure_reason">
              <span class="grey--text text-caption">Failure Reason</span><br>
              <span class="error--text">{{ selectedDelivery.failure_reason }}</span>
            </div>
          </v-card-text>
          <v-card-actions class="px-6 pb-6">
            <v-spacer></v-spacer>
            <v-btn text @click="showDeliveryDialog = false">Close</v-btn>
            <v-btn color="primary" outlined small :to="`/rmc-deliveries?booking_id=${selectedDelivery.booking_id}`" @click="showDeliveryDialog = false">
              Full Details
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "RmcBookingsPage",
  data() {
    return {
      bookings: [],
      count: 0,
      isLoading: false,
      filter: { status: "", booking_type: "", search: "" },
      showPaymentDialog: false,
      showDeliveryDialog: false,
      selectedBooking: null,
      selectedDelivery: null,
      statuses: ["pending", "confirmed", "active", "cancelled", "rejected"],
      types: ["on_demand", "dedicated", "full_takeover"],
      headers: [
        { text: "#", value: "sr_no", sortable: false, width: "50px" },
        { text: "Status", value: "status", width: "110px" },
        { text: "Type", value: "booking_type", width: "120px" },
        { text: "Plant", value: "plant", width: "160px" },
        { text: "Builder", value: "user", width: "150px" },
        { text: "Dates", value: "dates", width: "120px" },
        { text: "Capacity", value: "capacity", width: "90px" },
        { text: "Amount", value: "amount", width: "110px" },
        { text: "Payment", value: "payment", width: "100px" },
        { text: "Delivery Address", value: "delivery_address", width: "200px" },
        { text: "Actions", value: "actions", sortable: false, width: "200px" },
      ],
    };
  },
  computed: {
    filteredBookings() {
      if (!this.filter.search) return this.bookings;
      const q = this.filter.search.toLowerCase();
      return this.bookings.filter(b =>
        (b.plant && b.plant.name.toLowerCase().includes(q)) ||
        (b.user && (b.user.first_name || b.user.user_name || '').toLowerCase().includes(q)) ||
        (b.user && (b.user.phone || '').includes(q))
      );
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {};
        if (this.filter.status) params.status = this.filter.status;
        if (this.filter.booking_type) params.booking_type = this.filter.booking_type;
        const query = new URLSearchParams(params);
        const response = await axios.get(`rmc_bookings?${query}`);
        this.bookings = response.data.data || [];
        this.count = response.data.count || 0;
      } catch (e) {
        console.error("Error fetching bookings:", e);
      }
      this.isLoading = false;
    },
    async approve(id) {
      try {
        await axios.post(`rmc_bookings/approve/${id}`);
        this.getData();
      } catch (e) {
        console.error("Error approving:", e);
      }
    },
    async reject(id) {
      try {
        await axios.post(`rmc_bookings/reject/${id}`);
        this.getData();
      } catch (e) {
        console.error("Error rejecting:", e);
      }
    },
    async viewDelivery(bookingId) {
      try {
        const response = await axios.get(`rmc_deliveries?booking_id=${bookingId}`);
        const deliveries = response.data.data || [];
        if (deliveries.length) {
          this.selectedDelivery = deliveries[0];
          this.showDeliveryDialog = true;
        } else {
          alert("No delivery found for this booking.");
        }
      } catch (e) {
        console.error("Error fetching delivery:", e);
      }
    },
    statusColor(status) {
      const colors = { pending: "orange", confirmed: "success", active: "primary", cancelled: "grey", rejected: "error" };
      return colors[status] || "grey";
    },
    deliveryStatusColor(status) {
      const colors = { placed: "blue", confirmed: "success", loading: "orange", dispatched: "primary", in_transit: "purple", delivered: "success", failed: "error", re_delivery: "warning" };
      return colors[status] || "grey";
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    async verifyPaymentAction(id) {
      try {
        await axios.post(`rmc_bookings/verify_payment/${id}`);
        this.showPaymentDialog = false;
        this.getData();
      } catch (e) {
        console.error("Error verifying payment:", e);
      }
    },
    async rejectPaymentAction(id) {
      try {
        await axios.post(`rmc_bookings/reject_payment/${id}`);
        this.showPaymentDialog = false;
        this.getData();
      } catch (e) {
        console.error("Error rejecting payment:", e);
      }
    },
    showPaymentDetails(item) {
      this.selectedBooking = item;
      this.showPaymentDialog = true;
    },
    paymentColor(status) {
      const colors = { unpaid: "grey", submitted: "blue", verified: "success", rejected: "error" };
      return colors[status] || "grey";
    },
    getProofUrl(path) {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      return `https://blr1.vultrobjects.com/space-1/${path}`;
    },
    formatNumber(num) {
      return new Intl.NumberFormat("en-IN").format(parseFloat(num) || 0);
    },
  },
};
</script>
